import type { ImageFormat, MediaKind, VariantName } from './types/media.ts';

/**
 * What gets generated from an upload, and why.
 *
 * Shared so the frontend can reason about sizes without a round-trip, and so
 * the worker and the API cannot disagree about what a variant name means.
 */

export interface VariantSpec {
  name: Exclude<VariantName, 'original'>;
  /** Longest-edge target in CSS pixels, before the 2x factor below. */
  edge: number;
  format: ImageFormat;
  quality: number;
}

/**
 * Avatars are shown at 28–112px, so even the largest variant is small. They
 * are generated at 2x to stay sharp on dense displays.
 */
export const AVATAR_VARIANTS: readonly VariantSpec[] = Object.freeze([
  { name: 'thumb', edge: 64, format: 'webp', quality: 80 },
  { name: 'small', edge: 128, format: 'webp', quality: 80 },
  { name: 'medium', edge: 256, format: 'webp', quality: 82 },
]);

/** Covers span the full width of a profile header, so they need real sizes. */
export const COVER_VARIANTS: readonly VariantSpec[] = Object.freeze([
  { name: 'small', edge: 640, format: 'webp', quality: 78 },
  { name: 'medium', edge: 1280, format: 'webp', quality: 78 },
  { name: 'large', edge: 1920, format: 'webp', quality: 76 },
]);

/**
 * A story fills a phone screen and is looked at for a few seconds, so it needs
 * one good size rather than a ladder of them. `thumb` is the tray tile, which
 * is roughly 64x96 - small enough that the avatar-sized variant would do, and
 * cheap enough that generating it separately keeps the tray off the full-size
 * image entirely.
 */
export const STORY_VARIANTS: readonly VariantSpec[] = Object.freeze([
  { name: 'thumb', edge: 256, format: 'webp', quality: 78 },
  { name: 'large', edge: 1440, format: 'webp', quality: 76 },
]);

/**
 * A photo in a post.
 *
 * `small` is the grid tile - four of them share the width of one card, so it
 * is the size that is actually fetched most often and the one worth keeping
 * small. `large` is what the lightbox shows.
 */
export const POST_VARIANTS: readonly VariantSpec[] = Object.freeze([
  { name: 'small', edge: 480, format: 'webp', quality: 78 },
  { name: 'medium', edge: 960, format: 'webp', quality: 78 },
  { name: 'large', edge: 1600, format: 'webp', quality: 76 },
]);

/**
 * A recording has no image variants at all.
 *
 * Empty rather than absent, and spelled out rather than left to a `default:`
 * branch — which is what this function used to have. Under it, a voice asset
 * asked for three 640–1920px WebP covers, Sharp was handed a sound file, and
 * the upload was marked `failed` with the compiler perfectly satisfied, because
 * `default` made the function total over `MediaKind` by accident. The
 * derivative a recording does have is not a resize and is not described by a
 * pixel edge; the audio processor owns it.
 */
export const VOICE_VARIANTS: readonly VariantSpec[] = Object.freeze([]);

/**
 * Exhaustive on purpose: every kind names itself.
 *
 * The `never` below is the point. Add a kind to `MediaKind` and this stops
 * compiling until somebody says what that kind is made of, instead of silently
 * inheriting whatever the last branch happened to return.
 */
export function variantsFor(kind: MediaKind): readonly VariantSpec[] {
  switch (kind) {
    case 'avatar':
      return AVATAR_VARIANTS;
    case 'story':
      return STORY_VARIANTS;
    case 'post':
      return POST_VARIANTS;
    case 'voice':
      return VOICE_VARIANTS;
    case 'cover':
    case 'message':
    case 'listing':
      return COVER_VARIANTS;
    default: {
      const unhandled: never = kind;
      throw new Error(`no variant spec for media kind ${String(unhandled)}`);
    }
  }
}

/**
 * Ceilings applied before decoding.
 *
 * A "decompression bomb" is a small file that expands to an enormous bitmap —
 * a 10 KB PNG can declare 50 000 × 50 000 pixels and exhaust memory on decode.
 * Pixel count is therefore checked from the header before any processing.
 */
export const IMAGE_LIMITS = {
  /** Largest accepted upload, in bytes. */
  maxBytes: 5 * 1024 * 1024,
  /** Largest accepted decoded size, in pixels. 50 MP is far beyond any avatar. */
  maxPixels: 50_000_000,
  maxEdge: 10_000,
  /** Animated inputs are resized to their first frame only. */
  maxFrames: 1,
} as const;
